import Fuse from 'fuse.js';
import filter from 'lodash/fp/filter';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import maxBy from 'lodash/fp/maxBy';
import minBy from 'lodash/fp/minBy';
import slice from 'lodash/fp/slice';
import { MAX_AUTO_SUGGEST_ITEMS } from '../../common/constants';
import type { RootState } from '../../configuration/setup/store';
import { selectGeofences } from '../../store/data/dataSelectors';
import type { SuggestionAddress } from '../../store/search/searchSlice';
import type { CoordinatesWithAddress, Geofence } from './mappers/mapGeofence';

const getLat = (item: CoordinatesWithAddress): number => item.latitude;
const getLng = (item: CoordinatesWithAddress): number => item.longitude;

const getPolygonCenter = (geofence: Geofence): CoordinatesWithAddress | undefined => {
    const points = geofence.points;

    if (!points) {
        return;
    }

    const maxLat = maxBy(getLat)(points)?.latitude ?? 1;
    const maxLng = maxBy(getLng)(points)?.longitude ?? 1;

    const minLat = minBy(getLat)(points)?.latitude ?? 1;
    const minLng = minBy(getLng)(points)?.longitude ?? 1;

    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;

    const latitude = maxLat - latDiff / 2;
    const longitude = maxLng - lngDiff / 2;

    return { latitude, longitude };
};

const mapAddress = (geofence: Geofence): SuggestionAddress => {
    const position = geofence.center ?? getPolygonCenter(geofence);

    return {
        title: `${geofence.name ?? ''} ${geofence.center?.address ?? ''}`.trim(),
        type: geofence.type.toLowerCase(),
        resultType: geofence.type.toLowerCase(),
        position: { lat: position?.latitude, lng: position?.longitude },
    };
};

const withPosition = (geofence: Geofence) => geofence.center !== undefined || geofence.points !== undefined;

const processGeofences = flow(filter(withPosition), slice(0, MAX_AUTO_SUGGEST_ITEMS), map(mapAddress));

export const searchGeofenceAddresses = (getState: () => RootState, value: string) => {
    const geofences = selectGeofences(getState());
    const options = {
        keys: ['name', 'address'],
        threshold: 0.2,
        distance: 30,
    };
    const fuse = new Fuse(geofences, options);
    const results = fuse.search(value);
    return processGeofences(results.map(result => result.item));
};
